/**
 * ViewCampaignPage — Read-only page that displays full campaign details.
 * Data flow:
 *   URL params → useCampaignDetails → useCampaignForm(mode:'view') → rendered sections
 * Sections rendered (top to bottom):
 *   1. CampaignStatusSection (campaignId, version, status badge)
 *   2. CampaignForm (all fields disabled; includes SuspendedProducts under Seller Participation)
 *   3. Conflicted products (conditional — non-empty asins)
 *   4. AuditInfoSection (created/modified by/time, hasCampaignGoneLive)
 * Header actions:
 *   - Back to List (always)
 *   - Modify (ACTIVE | SCHEDULED | PROPAGATING only)
 *   - Cancel Campaign (ACTIVE | SCHEDULED | PROPAGATING only)
 */

import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@amzn/awsui-components-core/header';
import ContentLayout from '@amzn/awsui-components-core/content-layout';
import Alert from '@amzn/awsui-components-core/alert';
import Spinner from '@amzn/awsui-components-core/spinner';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Container from '@amzn/awsui-components-core/container';
import { useCampaignDetails } from 'src/hooks/useCampaignDetails';
import { useCampaignForm } from 'src/hooks/useCampaignForm';
import { useCancelCampaign } from 'src/hooks/useCancelCampaign';
import { useTranslate } from 'src/hooks/useTranslate';
import {
    SLUG_TO_CAMPAIGN_TYPE,
    CAMPAIGN_CONFIG,
    VALUE_MATCHING_BASE,
    buildRoute,
} from 'src/routes/valueMatching/routes';
import { CampaignStatus, CampaignType } from 'src/@types/valueMatching/enums';
import { CampaignForm } from 'src/components/organisms/CampaignForm';
import CampaignStatusSection from 'src/components/organisms/CampaignForm/sections/CampaignStatusSection';
import AuditInfoSection from 'src/components/organisms/CampaignForm/sections/AuditInfoSection';
import CancelCampaignModal from 'src/components/molecules/CancelCampaignModal';
import { trackClick, PAGE_IDS } from 'src/utils/monitoring';

const VIEW_CAMPAIGN_BUNDLE = 'pages.viewCampaign';

/**
 * Statuses that allow Modify and Cancel actions from the view page.
 * only SCHEDULED, PROPAGATING (transitional), and ACTIVE
 * campaigns are actionable. PAUSED_FUNDS_DEPLETED is NOT modifiable/cancellable.
 */
const ACTIONABLE_STATUSES = new Set<CampaignStatus>([
    CampaignStatus.SCHEDULED,
    CampaignStatus.PROPAGATING,
    CampaignStatus.ACTIVE,
]);

const MERCHANT_GROUP_TYPES = new Set([CampaignType.NO_COST_EMI, CampaignType.DEAL]);

const ViewCampaignPage: FC = () => {
    const {
        campaignType: slug = '',
        campaignId,
        version,
    } = useParams<{ campaignType: string; campaignId: string; version: string }>();

    const navigate = useNavigate();
    const { translate } = useTranslate(VIEW_CAMPAIGN_BUNDLE);

    const campaignType = SLUG_TO_CAMPAIGN_TYPE[slug];
    const campaignTypeName = campaignType ? CAMPAIGN_CONFIG[campaignType].displayName : slug;
    const showMerchantGroup = campaignType ? MERCHANT_GROUP_TYPES.has(campaignType) : false;

    const { campaignData, loading, error } = useCampaignDetails(campaignId, version, campaignType);

    // Initialise form state in view mode (config: null — all data from GetCampaignDetails)
    const form = useCampaignForm({
        config: null,
        campaignType,
        showMerchantGroup,
        mode: 'view',
        initialData: campaignData ?? undefined,
    });

    // Derive action button visibility from campaign status
    const status = campaignData?.campaignStatus;
    const showActionButtons = status != null && ACTIONABLE_STATUSES.has(status);

    // Single source of truth for the list-page route — used by Back-to-list
    // and the post-cancel navigation. Falls back to the value-matching root
    // when the slug is invalid (campaignType is undefined).
    const listRoute = campaignType ? buildRoute.list(campaignType) : VALUE_MATCHING_BASE;

    // ── Cancel campaign state ───────────────────────────────────────────
    const [cancelModalVisible, setCancelModalVisible] = useState(false);
    const {
        executeCancellation,
        cancelling,
        errorKey: cancelErrorKey,
        success: cancelSuccess,
        reset: resetCancel,
    } = useCancelCampaign();
    const { translate: translateCancelModal } = useTranslate('molecules.cancelCampaignModal');
    // Translate the cancel error at render time so a late-loading i18n bundle
    // still produces localized copy in the modal's inline Alert.
    const cancelError = cancelErrorKey ? translateCancelModal('error_message') : null;

    const handleCancelClick = () => {
        trackClick('CancelCampaignOpen', { page: PAGE_IDS.VIEW_CAMPAIGN, ...(campaignType && { campaignType }) });
        resetCancel();
        setCancelModalVisible(true);
    };

    const handleCancelDismiss = () => {
        if (cancelling) return;
        trackClick('CancelCampaignDismiss', { page: PAGE_IDS.VIEW_CAMPAIGN, ...(campaignType && { campaignType }) });
        setCancelModalVisible(false);
        resetCancel();
    };

    const handleCancelConfirm = () => {
        if (!campaignData?.campaignId || campaignData.version == null || !campaignType) return;
        trackClick('CancelCampaignConfirm', { page: PAGE_IDS.VIEW_CAMPAIGN, campaignType });
        executeCancellation(campaignData.campaignId, campaignData.version, campaignType);
    };

    // List-API GET is eventually consistent — hand the list page a payload
    // via router state so it can patch the row optimistically and refetch.
    const cancelledCampaignId = campaignData?.campaignId;
    const cancelledVersion = campaignData?.version;
    const cancelledTitle = campaignData?.title;
    useEffect(() => {
        if (cancelSuccess && cancelledCampaignId && cancelledVersion != null) {
            setCancelModalVisible(false);
            resetCancel();
            navigate(listRoute, {
                state: {
                    cancelSuccess: {
                        campaignId: cancelledCampaignId,
                        version: cancelledVersion,
                        title: cancelledTitle ?? '',
                    },
                },
            });
        }
    }, [cancelSuccess, navigate, listRoute, resetCancel, cancelledCampaignId, cancelledVersion, cancelledTitle]);

    // Navigation helpers
    const handleBackToList = () => {
        navigate(listRoute);
    };

    const handleModify = () => {
        if (campaignType && campaignId && version) {
            trackClick('ModifyCampaign', { page: PAGE_IDS.VIEW_CAMPAIGN, campaignType });
            navigate(buildRoute.modify(campaignType, campaignId, version));
        }
    };

    // Suspended products — show only when asins array is non-empty
    const suspendedAsins = campaignData?.suspendedProducts?.asins ?? [];
    const hasSuspendedProducts = suspendedAsins.length > 0;

    // Conflicted products — show only when asins array is non-empty
    const conflictedAsins = campaignData?.conflictedProducts?.asins ?? [];
    const hasConflictedProducts = conflictedAsins.length > 0;

    // ── Page header with action buttons ─────────────────────────────────
    const pageHeader = (
        <Header
            variant="h1"
            actions={
                <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="link" onClick={handleBackToList}>
                        {translate('back_to_list')}
                    </Button>
                    {showActionButtons && (
                        <>
                            <Button variant="normal" onClick={handleModify}>
                                {translate('modify_button')}
                            </Button>
                            <Button variant="primary" onClick={handleCancelClick}>
                                {translate('cancel_campaign_button')}
                            </Button>
                        </>
                    )}
                </SpaceBetween>
            }
        >
            {translate('view_title', { campaignType: campaignTypeName })}
        </Header>
    );

    if (loading) {
        return (
            <ContentLayout header={pageHeader}>
                <Box textAlign="center" padding={{ top: 'xxl' }}>
                    <Spinner size="large" />
                </Box>
            </ContentLayout>
        );
    }

    if (error) {
        return (
            <ContentLayout header={pageHeader}>
                <Alert type="error" header={translate('error_loading_details')}>
                    {error}
                </Alert>
            </ContentLayout>
        );
    }

    return (
        <ContentLayout header={pageHeader}>
            <SpaceBetween size="s">
                {/* Campaign Status Section — above the form */}
                {campaignData && <CampaignStatusSection campaignData={campaignData} />}

                {/* Campaign Form — all fields disabled in view mode;
                    suspended products appear under Seller Participation Conditions */}
                <CampaignForm
                    mode="view"
                    form={form}
                    config={null}
                    campaignType={campaignType}
                    showMerchantGroup={showMerchantGroup}
                    suspendedAsins={hasSuspendedProducts ? suspendedAsins : undefined}
                />

                {/* Conflicted Products — conditional read-only list */}
                {hasConflictedProducts && (
                    <Container
                        header={
                            <Header variant="h2">
                                {translate('conflicted_products_heading')}
                            </Header>
                        }
                    >
                        <Box data-testid="conflicted-asins-readonly">
                            {conflictedAsins.join(', ')}
                        </Box>
                    </Container>
                )}

                {/* Audit Info Section — below the form */}
                {campaignData && <AuditInfoSection campaignData={campaignData} />}
            </SpaceBetween>

            {/* Cancel campaign confirmation modal — only mounted while open
                so its translated content does not bleed into the page DOM. */}
            {cancelModalVisible && campaignData?.campaignId && (
                <CancelCampaignModal
                    visible
                    onDismiss={handleCancelDismiss}
                    onConfirm={handleCancelConfirm}
                    campaignId={campaignData.campaignId}
                    campaignTitle={campaignData.title ?? ''}
                    cancelling={cancelling}
                    error={cancelError}
                />
            )}
        </ContentLayout>
    );
};

export default ViewCampaignPage;
